/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getRichTextHtml, IActionContext, IImageSettings, Image, ITelemetry } from '@msdyn365-commerce/core';
import { SearchSuggestion } from '@msdyn365-commerce/retail-proxy';
import { getProductPageUrlSync, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    NodeTag
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IDobbiesSearchData } from '../dobbies-search.data';
import { IDobbiesSearchProps } from '../dobbies-search.props.autogenerated';

/**
 * Search product suggest resources.
 */
export interface ISearchProductSuggestResources {
    freePriceText: string;
}

export interface ISearchProductSuggestProps {
    searchProductSuggestClass: string;
    searchPageURL: string;
    searchQsp: string | undefined;
    hitPrefix: string;
    hitSuffix: string;
    autoSuggestProps: IDobbiesSearchProps<IDobbiesSearchData>;
    searchText: string;
    suggestions: SearchSuggestion[] | undefined;
    telemetry: ITelemetry;
    moduleType: string;
    moduleId: string;
    telemetryContent?: ITelemetryContent;
    resources?: ISearchProductSuggestResources;
}

export interface ISearchProductViewProps {
    title: React.ReactNode;
    items: ISearchProductItemViewProps[];
}

export interface ISearchProductItemViewProps {
    text: React.ReactNode;
    thumbnail: React.ReactNode;
    price: React.ReactNode;
    LiProduct: INodeProps;
    AProduct: INodeProps;
    id: string | undefined;
}

let payLoad: IPayLoad;
let telemetryContent: ITelemetryContent | undefined;

/**
 * ProductSuggestionsComponent.
 * @param props
 */
export const ProductSuggestionsComponent = (props: ISearchProductSuggestProps): ISearchProductViewProps | undefined => {
    const {
        suggestions,
        hitSuffix,
        hitPrefix,
        autoSuggestProps: {
            context: { actionContext },
            resources: { productSuggestionHeading }
        }
    } = props;
    const reactNodes: ISearchProductItemViewProps[] =
        suggestions?.map(item => _assembleNode(item, hitPrefix, hitSuffix, actionContext, props)) ?? [];
    telemetryContent = props.telemetryContent;
    payLoad = getPayloadObject('click', props.telemetryContent!, '');

    const titleNode = _renderTitle(productSuggestionHeading);

    return { title: titleNode, items: reactNodes };
};

const _renderTitle = (productSuggestionHeading: string) => {
    const productAutoSuggestionHeading = productSuggestionHeading ? productSuggestionHeading : 'Go to products';

    return <li className='msc-autoSuggest__productResults-title'>{productAutoSuggestionHeading}</li>;
};

const _assembleNode = (
    item: SearchSuggestion,
    hitPrefix: string,
    hitSuffix: string,
    actionContext: IActionContext,
    props: ISearchProductSuggestProps
): ISearchProductItemViewProps => {
    const altText =
        item.Value && item.Value.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '').replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');

    const suggestionPrice = _getSuggestionPrice(item);
    return {
        text: _getHighlightedText(item.Value ? item.Value : '', hitPrefix, hitSuffix),
        thumbnail: _renderThumbnail(item, altText ? altText : '', props),
        price: !ObjectExtensions.isNullOrUndefined(suggestionPrice) && _renderPrice(suggestionPrice, props),
        LiProduct: _getLiWrapper(),
        AProduct: _getAWrapper(item, hitPrefix, hitSuffix, actionContext, props, suggestionPrice),
        id: item.Id
    };
};

const _getLiWrapper = () => {
    return {
        className: 'msc-autoSuggest__productResults-item',
        tag: 'li' as NodeTag
    };
};

const _getAWrapper = (
    item: SearchSuggestion,
    hitPrefix: string,
    hitSuffix: string,
    actionContext: IActionContext,
    props: ISearchProductSuggestProps,
    suggestionPrice?: number
) => {
    const {
        autoSuggestProps: { context }
    } = props;
    const ariaLabel =
        item.Value && item.Value.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '').replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');

    payLoad.contentAction.recid = item.Id;
    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);
    const productPrice =
        !ObjectExtensions.isNullOrUndefined(suggestionPrice) &&
        context.cultureFormatter.formatCurrency(suggestionPrice, context.request.channel?.Currency);

    return {
        className: 'msc-autoSuggest__productResults-item',
        tag: 'a' as NodeTag,
        'aria-label': productPrice ? `${ariaLabel!} ${productPrice}` : ariaLabel,
        href: _generateLink(item, hitPrefix, hitSuffix, actionContext),
        ...attribute
    };
};

const _getSuggestionPrice = (suggestion: SearchSuggestion): number | undefined => {
    const directPrice =
        _extractPriceFromCommerceProperty(suggestion.Value3) ??
        _extractPriceFromCommerceProperty(suggestion.Value2) ??
        _extractPriceFromCommerceProperty(suggestion.Value4);

    if (!ObjectExtensions.isNullOrUndefined(directPrice)) {
        return directPrice;
    }

    const adjustedPriceProperty = suggestion.ExtensionProperties?.find(property => {
        const key = property.Key?.toLowerCase();
        return key === 'adjustedprice' || key === 'price' || key === 'customercontextualprice';
    });

    return _extractPriceFromCommerceProperty(adjustedPriceProperty);
};

const _extractPriceFromCommerceProperty = (property?: { Value?: { DecimalValue?: number; StringValue?: string } }): number | undefined => {
    if (!property || !property.Value) {
        return undefined;
    }

    const decimalPrice = property.Value.DecimalValue;
    if (!ObjectExtensions.isNullOrUndefined(decimalPrice)) {
        return decimalPrice;
    }

    return _extractNumericValue(property.Value.StringValue);
};

const _extractNumericValue = (value?: string): number | undefined => {
    if (!value) {
        return undefined;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const _generateLink = (suggestion: SearchSuggestion, hitPrefix: string, hitSuffix: string, actionContext: IActionContext) => {
    const formattedLink = (suggestion.Value as string)
        .replace(new RegExp(`[${hitPrefix}]`, 'gi'), '')
        .replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');
    return suggestion.Value && suggestion.Id
        ? getProductPageUrlSync(formattedLink, (suggestion.Id as unknown) as number, actionContext, undefined)
        : '';
};

const _getHighlightedText = (suggestion: string, hitPrefix: string, hitSuffix: string): JSX.Element => {
    const formattedText = suggestion
        .replace(new RegExp(`[${hitPrefix}]`, 'gi'), '<strong>')
        .replace(new RegExp(`[${hitSuffix}]`, 'gi'), '</strong>');

    return (
        // eslint-disable-next-line react/no-danger
        <span className='msc-price msc-autoSuggest__productResults-item-text' dangerouslySetInnerHTML={getRichTextHtml(formattedText)} />
    );
};

const _renderPrice = (adjustedPrice: number, props: ISearchProductSuggestProps): JSX.Element | null => {
    const {
        autoSuggestProps: { context }
    } = props;

    const formattedPrice = context.cultureFormatter.formatCurrency(adjustedPrice, context.request.channel?.Currency);

    return <span className='msc-autoSuggest__productResults-item-price'>{formattedPrice || props.resources?.freePriceText}</span>;
};

const _renderThumbnail = (suggestion: SearchSuggestion, altText: string, props: ISearchProductSuggestProps): JSX.Element | null => {
    const {
        autoSuggestProps: {
            context: {
                request: { gridSettings }
            },
            config: { imageSettings }
        }
    } = props;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }

    if (!suggestion || !suggestion.ImageUrl) {
        props.telemetry.error('Search suggestion content is empty, module wont render.');
        return null;
    }

    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=40&h=40&m=6', w: 0, h: 0 },
            lg: { q: 'w=40&h=40&m=6', w: 0, h: 0 },
            xl: { q: 'w=40&h=40&m=6', w: 0, h: 0 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    return (
        <Image
            requestContext={props.autoSuggestProps.context.actionContext.requestContext}
            altText={altText}
            className='msc-autoSuggest__productResults-item-img'
            src={suggestion.ImageUrl}
            gridSettings={gridSettings!}
            imageSettings={imageSettings ? imageSettings : defaultImageSettings}
            loadFailureBehavior='empty'
        />
    );
};
